import { defineStore } from 'pinia'
import debug from '../../utils/console'
import type { RevContentAd } from '../../ad_ops/types'

const logger = debug.withTag('revContentStore')

interface RevContentStoreState {
  ads: RevContentAd[]
  currentIndex: number
  offset: number
}

const REV_CONTENT_API_ENDPOINT = '/api/v2/revcontent'
const REV_CONTENT_POSTBACK_ENDPOINT = 'https://trends.revcontent.com/view.php'

export const useRevContentStore = defineStore('revContent', {
  state: (): RevContentStoreState => ({
    ads: [],
    currentIndex: 0,
    offset: 0,
  }),

  actions: {
    async splitTestCheck() {
      const { client } = await useOpenFeature()
      const isRevContentEnabled = client.getStringValue(
        'splittestname',
        'default'
      )
      return isRevContentEnabled === 'sp7_rev_content_native'
    },
    async init() {
      if (!(await this.splitTestCheck())) return
      try {
        const INIT_COUNT = 3
        await this.fetchAds(INIT_COUNT)
      } catch (e) {
        logger.error('Failed to initialize and populate RevContent store', e)
      }
    },
    async fetchAds(count?: number) {
      if (!(await this.splitTestCheck())) return
      try {
        const res = await fetch(
          `${REV_CONTENT_API_ENDPOINT}?offset=${this.offset}&count=${count || 1}`,
          {
            method: 'POST',
          }
        )
        if (!res.ok) {
          throw new Error(`Failed to fetch RevContent ads : ${res.statusText}`)
        }
        const data = await res.json()

        this.offset = this.offset + (count || 1)
        this.ads = [...this.ads, ...data.content]
        return data.content[0]
      } catch (e) {
        logger.error('Failed to fetch RevContent ads', e)
        return []
      }
    },
    async getNextAd() {
      const ad = { ...this.ads[this.currentIndex] }
      if (this.currentIndex === 0) ad.view_type = 'widget'
      else ad.view_type = 'fill'
      this.currentIndex = this.currentIndex + 1

      if (!ad.headline) {
        const newAd = await this.fetchAds(1)
        if (newAd) {
          newAd.view_type = 'fill'
          return newAd
        }
      }
      return ad
    },
    async postView(ad: RevContentAd) {
      try {
        const pixel = new URLSearchParams([
          ['view', decodeURIComponent(ad.viewHash ?? '')],
          ['p[]', ad.index?.toString() ?? '0'],
          ['view_type', ad.view_type ?? 'fill'],
        ])

        await fetch(`${REV_CONTENT_POSTBACK_ENDPOINT}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          },
          body: pixel,
        })
      } catch (error) {
        logger.error('Failed to post view for RevContent ad', error)
      }
    },
  },
})
